import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    OutlinedInput,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import api from "../../../utils/api";
import Table from "../../../components/Table";
import ActionsMenu from "../../../components/ActionsMenu";
import { formatMoney } from "../../../utils/helpers";
import { SalaryGroupColumns } from "./components/SalaryGroupColumns";
import SalaryDetailsDrawer from "./components/SalaryDetailsDrawer";

function getLastMonthDateRangeISO() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    const fmt = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from: fmt(start), to: fmt(end) };
}

function SummaryCard({ label, value, color = "text.primary" }) {
    return (
        <Card sx={{ minWidth: 220 }}>
            <CardContent>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h6" sx={{ color, mt: 0.5 }}>{value}</Typography>
            </CardContent>
        </Card>
    );
}

function teacherLabel(t) {
    if (!t) return "";
    const name = `${t.first_name || ""} ${t.last_name || ""}`.trim();
    return name || t.email || `Teacher #${t.id}`;
}

export default function Salaries() {
    const defaults = useMemo(() => getLastMonthDateRangeISO(), []);
    const [from, setFrom] = useState(defaults.from);
    const [to, setTo] = useState(defaults.to);
    const [teacher, setTeacher] = useState(null);
    const [teachers, setTeachers] = useState([]);
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ total_lessons: 0, total_amount: 0 });
    const [loading, setLoading] = useState(false);
    const [openDetails, setOpenDetails] = useState(null);

    const columns = useMemo(() => SalaryGroupColumns(), []);

    const loadTeachers = useCallback(async () => {
        try {
            const res = await api.get("admin/teachers", { params: { page: 1, perPage: 500 } });
            setTeachers(res.data?.data || []);
        } catch (err) {
            console.error("Failed to load teachers", err);
        }
    }, []);

    const loadGroups = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("admin/income-expenses/salary/groups", {
                params: {
                    from: from || undefined,
                    to: to || undefined,
                    teacher_id: teacher?.id || undefined,
                },
            });
            setRows(res.data?.data || []);
            setMeta(res.data?.meta || { total_lessons: 0, total_amount: 0 });
        } catch (err) {
            console.error("Failed to load salary groups", err);
            setRows([]);
            setMeta({ total_lessons: 0, total_amount: 0 });
        } finally {
            setLoading(false);
        }
    }, [from, to, teacher?.id]);

    useEffect(() => {
        loadTeachers();
    }, [loadTeachers]);

    useEffect(() => {
        loadGroups();
    }, [loadGroups]);

    const deleteGroup = useCallback(async (group) => {
        if (!group?.teacher_id || !group?.period_month) return;
        const label = `${group.teacher_name || "Teacher #" + group.teacher_id} • ${group.period_month}`;
        if (!window.confirm(`Delete every salary entry in "${label}"? This cannot be undone.`)) return;
        try {
            await api.delete("admin/income-expenses/salary/groups", {
                params: {
                    teacher_id: group.teacher_id,
                    period: group.period_month,
                },
            });
            loadGroups();
        } catch (err) {
            window.alert(err?.response?.data?.message || "Could not delete salary group.");
        }
    }, [loadGroups]);

    const exportToExcel = () => {
        const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
        const url = new URL(`${base}/admin/income-expenses/salary/export`, window.location.origin);
        if (from) url.searchParams.set("from", from);
        if (to) url.searchParams.set("to", to);
        if (teacher?.id) url.searchParams.set("teacher_id", String(teacher.id));
        const token =
            new URLSearchParams(window.location.search).get("token") ||
            localStorage.getItem("sessionID");
        if (token) url.searchParams.set("token", token);
        window.open(url.toString(), "_blank");
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <Typography component="h2" variant="h6" sx={{ m: 0 }}>
                    Salaries
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={exportToExcel}
                >
                    Export to Excel
                </Button>
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <SummaryCard
                    label="Total Lessons"
                    value={String(meta.total_lessons || 0)}
                />
                <SummaryCard
                    label="Total Salary"
                    value={formatMoney(Number(meta.total_amount || 0))}
                    color="error.main"
                />
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gap: 1.5,
                    mb: 2,
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(4, minmax(0, 1fr))",
                    },
                }}
            >
                <OutlinedInput
                    size="small"
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                />
                <OutlinedInput
                    size="small"
                    type="date"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                />
                <Autocomplete
                    size="small"
                    options={teachers}
                    value={teacher}
                    getOptionLabel={teacherLabel}
                    isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                    onChange={(_, val) => setTeacher(val || null)}
                    renderInput={(params) => <TextField {...params} placeholder="All teachers" label="Teacher" />}
                />
                <Button
                    variant="outlined"
                    onClick={() => {
                        const r = getLastMonthDateRangeISO();
                        setFrom(r.from);
                        setTo(r.to);
                        setTeacher(null);
                    }}
                >
                    Reset
                </Button>
            </Box>

            <Table
                loading={loading}
                rows={rows.map((r, idx) => ({ ...r, id: `${r.teacher_id}_${r.period_month}_${idx}` }))}
                columns={columns}
                selactable={false}
                draggable={false}
                renderActions={(row) => (
                    <ActionsMenu
                        actions={[
                            {
                                label: (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <VisibilityIcon fontSize="small" />
                                        <span>View details</span>
                                    </Stack>
                                ),
                                onClick: () => setOpenDetails(row),
                            },
                            {
                                label: (
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "error.main" }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                        <span>Delete group</span>
                                    </Stack>
                                ),
                                onClick: () => deleteGroup(row),
                            },
                        ]}
                    />
                )}
            />

            <SalaryDetailsDrawer
                open={Boolean(openDetails)}
                group={openDetails}
                onClose={() => setOpenDetails(null)}
                onChanged={() => loadGroups()}
            />
        </Box>
    );
}
